const ALLOWED = new Set([0, 90, 180, 270]);
/** Coerce sync / UI values to a supported mount angle (unknown → 0). */
export function normalizeRotationDegrees(value) {
    if (typeof value === 'number' && ALLOWED.has(value)) {
        return value;
    }
    return 0;
}
/**
 * Portrait mounts swap axes: after a 90°/270° rotate, the stage must be
 * parentHeight × parentWidth so it fills the physical panel exactly.
 * Dimensions are rounded to whole CSS pixels to avoid seam/glitch lines.
 */
export function computeStageLayout(parentWidthPx, parentHeightPx, degrees) {
    const normalized = normalizeRotationDegrees(degrees);
    const width = Math.max(0, Math.round(parentWidthPx));
    const height = Math.max(0, Math.round(parentHeightPx));
    if (normalized === 90 || normalized === 270) {
        return {
            degrees: normalized,
            widthPx: height,
            heightPx: width,
            centered: true,
        };
    }
    return {
        degrees: normalized,
        widthPx: width,
        heightPx: height,
        centered: false,
    };
}
export function stageLayoutsEqual(a, b) {
    if (!a || !b)
        return false;
    return (a.degrees === b.degrees &&
        a.parentWidthPx === b.parentWidthPx &&
        a.parentHeightPx === b.parentHeightPx &&
        a.widthPx === b.widthPx &&
        a.heightPx === b.heightPx &&
        a.centered === b.centered);
}
function clearInlineStageGeometry(root) {
    root.style.inset = '';
    root.style.top = '';
    root.style.left = '';
    root.style.right = '';
    root.style.bottom = '';
    root.style.width = '';
    root.style.height = '';
    root.style.transform = '';
}
function writeStageLayout(root, layout) {
    root.dataset.rotation = String(layout.degrees);
    if (layout.centered) {
        root.style.inset = 'auto';
        root.style.top = '50%';
        root.style.left = '50%';
        root.style.right = 'auto';
        root.style.bottom = 'auto';
        root.style.width = `${layout.widthPx}px`;
        root.style.height = `${layout.heightPx}px`;
        root.style.transform = `translate(-50%, -50%) rotate(${layout.degrees}deg)`;
        return;
    }
    root.style.inset = '0';
    root.style.top = '0';
    root.style.left = '0';
    root.style.right = '0';
    root.style.bottom = '0';
    root.style.width = 'auto';
    root.style.height = 'auto';
    root.style.transform = layout.degrees === 180 ? 'rotate(180deg)' : 'none';
}
/**
 * Sizes and rotates the playback stage from the parent element's pixel box.
 * Prefer this over 100vh/100vw — viewport units drift with browser chrome and
 * cause letterbox seams on Fire TV / kiosk shells.
 *
 * Pass `previous` to skip identical writes (avoids compositor flicker on every
 * media swap / ResizeObserver tick / sync).
 */
export function applyStageRotation(root, degrees, previous) {
    const normalized = normalizeRotationDegrees(degrees);
    const parent = root.parentElement;
    if (!parent) {
        clearInlineStageGeometry(root);
        root.dataset.rotation = String(normalized);
        return null;
    }
    const parentWidthPx = parent.clientWidth;
    const parentHeightPx = parent.clientHeight;
    if (parentWidthPx <= 0 || parentHeightPx <= 0) {
        return previous ?? null;
    }
    const layout = computeStageLayout(parentWidthPx, parentHeightPx, normalized);
    const snapshot = {
        ...layout,
        parentWidthPx: Math.max(0, Math.round(parentWidthPx)),
        parentHeightPx: Math.max(0, Math.round(parentHeightPx)),
    };
    if (stageLayoutsEqual(previous, snapshot)) {
        return previous ?? snapshot;
    }
    writeStageLayout(root, layout);
    return snapshot;
}
//# sourceMappingURL=stage-rotation.js.map